import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Avatar,
  Badge,
  Tooltip,
  Fade,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { EntityPermissionsSectionProps } from '../types';
import { entityConfig, permissionConfig } from '../constants/entityConfig';
import { getEntityName, getEntitySecondaryInfo } from '../utils/entityUtils';
import { AddPermissionDialog } from './AddPermissionDialog';

export const EntityPermissionsSection: React.FC<
  EntityPermissionsSectionProps
> = ({ entityType, roleId, permissions, onDelete, onAdd, onEdit }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const config = entityConfig[entityType];

  // Filtrar permisos para esta entidad
  const entityPermissions = permissions.filter(
    p => p.entityType === entityType
  );

  console.log(`📋 ${entityType} - Permisos filtrados:`, entityPermissions);

  return (
    <Card
      sx={{
        mb: 3,
        border: `2px solid ${config.color}20`,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardHeader
        onClick={() => setExpanded(!expanded)}
        avatar={
          <Avatar
            sx={{
              bgcolor: config.bgColor,
              color: config.color,
              width: 56,
              height: 56,
              boxShadow: `0 4px 12px ${config.color}30`,
            }}
          >
            {config.icon}
          </Avatar>
        }
        title={
          <Typography
            variant='h6'
            sx={{ fontWeight: 'bold', color: config.color }}
          >
            {config.name}
          </Typography>
        }
        subheader={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Typography variant='body2' color='textSecondary'>
              {config.description}
            </Typography>
            <Badge
              badgeContent={entityPermissions.length}
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: config.color,
                  color: 'white',
                  fontWeight: 'bold',
                },
              }}
            >
              <SecurityIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </Badge>
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              variant='contained'
              size='medium'
              sx={{
                bgcolor: config.color,
                fontWeight: 'bold',
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  bgcolor: config.color,
                  filter: 'brightness(0.9)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Agregar
            </Button>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                color: config.color,
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        }
        sx={{
          bgcolor: `${config.color}08`,
          borderBottom: `2px solid ${config.color}15`,
          py: 2,
        }}
      />

      <Collapse in={expanded}>
        <CardContent sx={{ p: 0 }}>
          {entityPermissions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: `${config.color}15`,
                  color: config.color,
                  width: 80,
                  height: 80,
                  margin: '0 auto 16px',
                }}
              >
                <SecurityIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant='h6' color='textSecondary' gutterBottom>
                No hay permisos asignados
              </Typography>
              <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
                Haz clic en "Agregar" para asignar permisos específicos a{' '}
                {config.name.toLowerCase()}
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
                variant='outlined'
                sx={{
                  borderColor: config.color,
                  color: config.color,
                  '&:hover': {
                    bgcolor: config.bgColor,
                    borderColor: config.color,
                  },
                }}
              >
                Agregar primer permiso
              </Button>
            </Box>
          ) : (
            <List dense sx={{ py: 0 }}>
              {entityPermissions.map((permission, index) => {
                const permissionInfo =
                  permissionConfig[permission.permissionLevel];
                return (
                  <Fade in key={permission.id} timeout={300 + index * 100}>
                    <ListItem
                      sx={{
                        borderBottom:
                          index < entityPermissions.length - 1
                            ? `1px solid ${config.color}10`
                            : 'none',
                        py: 2,
                        px: 3,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: `${config.color}05`,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: permissionInfo?.bgColor || 'grey.100',
                          color: permissionInfo?.color || 'grey.600',
                          width: 40,
                          height: 40,
                          mr: 2,
                          boxShadow: `0 2px 8px ${permissionInfo?.color}30`,
                        }}
                      >
                        {permissionInfo?.icon}
                      </Avatar>

                      <ListItemText
                        primary={
                          <Typography
                            variant='subtitle1'
                            sx={{ fontWeight: 'bold' }}
                          >
                            {getEntityName(permission)}
                          </Typography>
                        }
                        secondary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <Chip
                              icon={permissionInfo?.icon}
                              label={
                                permissionInfo?.label ||
                                permission.permissionLevel
                              }
                              sx={{
                                bgcolor: permissionInfo?.bgColor,
                                color: permissionInfo?.color,
                                fontWeight: 'bold',
                                height: 28,
                                '& .MuiChip-icon': {
                                  color: permissionInfo?.color,
                                },
                              }}
                              size='small'
                            />
                            {getEntitySecondaryInfo(permission) && (
                              <Typography
                                variant='caption'
                                color='textSecondary'
                              >
                                • {getEntitySecondaryInfo(permission)}
                              </Typography>
                            )}
                            <Typography variant='caption' color='textSecondary'>
                              • ID: {permission.entityId}
                            </Typography>
                          </Box>
                        }
                      />

                      <ListItemSecondaryAction>
                        <Tooltip title='Editar permiso' arrow>
                          <IconButton
                            edge='end'
                            onClick={() => onEdit(permission)}
                            size='medium'
                            sx={{
                              color: 'primary.main',
                              mr: 1,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Eliminar permiso' arrow>
                          <IconButton
                            edge='end'
                            onClick={() => onDelete(permission)}
                            size='medium'
                            sx={{
                              color: 'error.main',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Fade>
                );
              })}
            </List>
          )}
        </CardContent>
      </Collapse>

      <AddPermissionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        entityType={entityType}
        roleId={roleId}
        onAdd={onAdd}
        existingPermissions={permissions}
      />
    </Card>
  );
};
